import { forwardRef, useId, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes } from 'react'
import type { TextareaHTMLAttributes } from 'react'
import { cn } from '../lib/cn'
import { IconeAlerta } from './Icones'

/**
 * O foco troca o `outline` global por um anel da MESMA cor e espessura.
 *
 * Não é o anel que substitui o indicador de foco: é o mesmo indicador desenhado por dentro
 * da borda arredondada do campo, para não sair duplicado (anel + contorno) quando o campo
 * recebe foco por teclado. `focus:` e não `focus-visible:` porque, em campo de digitação,
 * quem clicou também precisa ver onde vai digitar.
 */
const CAIXA =
  'block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm ' +
  'transition-colors placeholder:text-slate-400 ' +
  'focus:border-niteroi-600 focus:outline-none focus:ring-2 focus:ring-niteroi-600 ' +
  'disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500 ' +
  'aria-[invalid=true]:border-red-500 aria-[invalid=true]:focus:border-red-600 ' +
  'aria-[invalid=true]:focus:ring-red-500'

interface Moldura {
  rotulo: string
  ajuda?: string
  erro?: string
  /**
   * A interrogação ao lado do rótulo (`<Explicacao>`), para quando o porquê do campo é longo.
   *
   * Fora do `<label>`, de propósito: dentro dele, o botão entraria no nome acessível do campo e o
   * leitor de tela leria "Mês de referência O que significa Mês de referência?".
   */
  explicacao?: ReactNode
}

/** O erro, com ícone: cor sozinha não é sinal para quem não distingue vermelho de cinza. */
function Erro({ id, texto }: { id: string; texto: string }) {
  return (
    <p id={id} className="mt-1 flex items-start gap-1 text-xs font-medium text-red-700">
      <IconeAlerta className="mt-px h-3.5 w-3.5" />
      <span>{texto}</span>
    </p>
  )
}

/**
 * A moldura de um campo: rótulo, ajuda e erro, todos ligados por `aria-describedby`.
 *
 * Ajuda e erro precisam estar ligados ao controle, não apenas próximos dele — para
 * quem usa leitor de tela, texto solto ao lado do campo simplesmente não existe. Como é
 * fácil esquecer essa ligação campo a campo, ela mora aqui e não em cada formulário.
 */
function Envolver({
  rotulo,
  ajuda,
  erro,
  explicacao,
  id,
  idAjuda,
  idErro,
  children,
}: Moldura & { id: string; idAjuda: string; idErro: string; children: ReactNode }) {
  return (
    <div>
      <div className="flex items-center gap-1.5">
        <label htmlFor={id} className="block text-sm font-medium text-slate-800">
          {rotulo}
        </label>
        {explicacao}
      </div>
      {ajuda && (
        <p id={idAjuda} className="mt-0.5 text-xs text-slate-500">
          {ajuda}
        </p>
      )}
      <div className="mt-1.5">{children}</div>
      {erro && <Erro id={idErro} texto={erro} />}
    </div>
  )
}

function ids(base: string) {
  return { id: base, idAjuda: `${base}-ajuda`, idErro: `${base}-erro` }
}

function descritores(ajuda: string | undefined, erro: string | undefined, base: string): string | undefined {
  const lista = [ajuda ? `${base}-ajuda` : null, erro ? `${base}-erro` : null].filter(Boolean)
  return lista.length > 0 ? lista.join(' ') : undefined
}

type PropsTexto = Moldura & InputHTMLAttributes<HTMLInputElement>

/** Campo de uma linha. */
export const CampoDeTexto = forwardRef<HTMLInputElement, PropsTexto>(function CampoDeTexto(
  { rotulo, ajuda, erro, className, ...resto },
  ref,
) {
  const base = useId()
  const { id, idAjuda, idErro } = ids(base)
  return (
    <Envolver rotulo={rotulo} ajuda={ajuda} erro={erro} id={id} idAjuda={idAjuda} idErro={idErro}>
      <input
        ref={ref}
        id={id}
        aria-invalid={erro ? true : undefined}
        aria-describedby={descritores(ajuda, erro, base)}
        className={cn(CAIXA, className)}
        {...resto}
      />
    </Envolver>
  )
})

type PropsSugestoes = PropsTexto & { sugestoes: readonly string[] }

/**
 * Campo de uma linha que oferece as respostas conhecidas sem proibir as outras.
 *
 * Existe para o caso em que há um vocabulário praticado mas ele não é fechado: a unidade de um
 * item de ordem de serviço é 'Mês', 'PF' ou 'UST' em todo documento que a fiscalização já emitiu —
 * e ainda assim é o papel que manda, porque o TRP praticado imprime 'Mês' numa tabela e 'UST' em
 * outra para o MESMO item. Uma caixa de seleção obrigaria a escolher entre inventar uma opção e
 * escrever o que o documento não diz; um campo de texto puro faz o usuário adivinhar a grafia, e
 * 'mes', 'Mês' e 'MÊS' viram três unidades diferentes no relatório.
 *
 * O `datalist` do HTML resolve os dois: a lista aparece ao focar, e quem precisa escrever outra
 * coisa escreve.
 */
export const CampoDeTextoComSugestoes = forwardRef<HTMLInputElement, PropsSugestoes>(
  function CampoDeTextoComSugestoes({ rotulo, ajuda, erro, className, sugestoes, ...resto }, ref) {
    const base = useId()
    const { id, idAjuda, idErro } = ids(base)
    const idLista = `${base}-sugestoes`
    return (
      <Envolver rotulo={rotulo} ajuda={ajuda} erro={erro} id={id} idAjuda={idAjuda} idErro={idErro}>
        <input
          ref={ref}
          id={id}
          list={idLista}
          aria-invalid={erro ? true : undefined}
          aria-describedby={descritores(ajuda, erro, base)}
          className={cn(CAIXA, className)}
          {...resto}
        />
        <datalist id={idLista}>
          {sugestoes.map((sugestao) => (
            <option key={sugestao} value={sugestao} />
          ))}
        </datalist>
      </Envolver>
    )
  },
)

type PropsArea = Moldura & TextareaHTMLAttributes<HTMLTextAreaElement>

/** Campo de texto longo, para os artefatos do plano. */
export const CampoDeTextoLongo = forwardRef<HTMLTextAreaElement, PropsArea>(function CampoDeTextoLongo(
  { rotulo, ajuda, erro, className, rows = 4, ...resto },
  ref,
) {
  const base = useId()
  const { id, idAjuda, idErro } = ids(base)
  return (
    <Envolver rotulo={rotulo} ajuda={ajuda} erro={erro} id={id} idAjuda={idAjuda} idErro={idErro}>
      <textarea
        ref={ref}
        id={id}
        rows={rows}
        aria-invalid={erro ? true : undefined}
        aria-describedby={descritores(ajuda, erro, base)}
        className={cn(CAIXA, className)}
        {...resto}
      />
    </Envolver>
  )
})

type PropsSelecao = Moldura & SelectHTMLAttributes<HTMLSelectElement> & { children: ReactNode }

/** Campo de escolha única. */
export const CampoDeSelecao = forwardRef<HTMLSelectElement, PropsSelecao>(function CampoDeSelecao(
  { rotulo, ajuda, erro, explicacao, className, children, ...resto },
  ref,
) {
  const base = useId()
  const { id, idAjuda, idErro } = ids(base)
  return (
    <Envolver
      rotulo={rotulo}
      ajuda={ajuda}
      erro={erro}
      explicacao={explicacao}
      id={id}
      idAjuda={idAjuda}
      idErro={idErro}
    >
      <select
        ref={ref}
        id={id}
        aria-invalid={erro ? true : undefined}
        aria-describedby={descritores(ajuda, erro, base)}
        className={cn(CAIXA, className)}
        {...resto}
      >
        {children}
      </select>
    </Envolver>
  )
})

type PropsCaixa = Moldura & InputHTMLAttributes<HTMLInputElement>

/**
 * Caixa de marcar, com a mesma moldura acessível dos demais campos.
 *
 * O rótulo fica ao lado, e não acima: em caixa de marcar, rótulo acima faz o alvo de clique
 * ser só o quadradinho de 16px. Ligado por `htmlFor`, a área clicável passa a ser o texto
 * inteiro — o que importa em tela pequena e para quem tem pouca precisão no toque.
 *
 * A cor vem de `accent-color`, e não de um plugin de formulários: é o próprio controle do
 * navegador pintado de azul institucional, então ele continua sendo a caixa nativa que o
 * leitor de tela e o teclado já sabem operar.
 *
 * @param props rótulo, ajuda, erro e os atributos de `input`
 * @returns a caixa com rótulo
 */
export const CampoDeCaixa = forwardRef<HTMLInputElement, PropsCaixa>(function CampoDeCaixa(
  { rotulo, ajuda, erro, className, ...resto },
  ref,
) {
  const base = useId()
  const { id, idAjuda, idErro } = ids(base)
  return (
    <div>
      <div className="flex gap-2.5">
        <input
          ref={ref}
          id={id}
          type="checkbox"
          aria-invalid={erro ? true : undefined}
          aria-describedby={descritores(ajuda, erro, base)}
          className={cn('mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 accent-niteroi-700', className)}
          {...resto}
        />
        <div className="min-w-0">
          <label htmlFor={id} className="cursor-pointer text-sm font-medium text-slate-800">
            {rotulo}
          </label>
          {ajuda && (
            <p id={idAjuda} className="mt-0.5 text-xs text-slate-500">
              {ajuda}
            </p>
          )}
        </div>
      </div>
      {erro && <Erro id={idErro} texto={erro} />}
    </div>
  )
})
